import React, { useEffect, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { Link } from '~/lib/react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';
import * as libRegions from '@luxuryescapes/lib-regions';

import CopyableField from '~/components/Common/CopyableField';
import LuxLoyaltyPackageTargetUpgradesWarning from '~/components/LuxLoyalty/LuxLoyaltyPackageTargetUpgradesWarning';
import {
  isAccommodationPackage,
  isHotelOfferType,
  isTourOfferType,
  isTourPackage,
} from '~/components/Offers/Detail/typeNarrowingUtils';

import { ExperienceOffer } from '~/services/ExperiencesService';

import { arrayToObject } from '~/utils/arrayUtils';
import { getPackageName } from '~/utils/experienceUtils';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';
import { isOfferTypeHotel } from '~/utils/offer';

import { OFFER_TYPE_BUNDLE_AND_SAVE } from '../../../consts/offerTypes';

interface Props {
  offer: {
    result: App.Offer;
  };
  experiencesByPackageId: Record<string, Array<ExperienceOffer>>;
}

type PackageWithExperiences = App.Package & { svc_experiences?: Array<App.Experience> };

function sortPackagesByCost(packages: Array<App.Package>): Array<App.Package> {
  return [...packages].sort((a, b) => (a.cost_price ?? 0) - (b.cost_price ?? 0));
}

export default function PackagesPane({ offer, experiencesByPackageId }: Props) {
  const [packages, setPackages] = useState<Array<PackageWithExperiences>>(sortPackagesByCost(offer.result.packages));
  const [showPackageTargetUpgradesWarning, setShowPackageTargetUpgradesWarning] = useState(false);
  const [showPackageTargetUpgradesDevWarning, setShowPackageTargetUpgradesDevWarning] = useState(false);

  useEffect(() => {
    const updatedPackages = offer.result.packages.map((pkg) => ({
      ...pkg,
      svc_experiences: experiencesByPackageId[pkg.id_salesforce_external]?.filter(
        (experience) => experience.status === 'ONLINE',
      ),
    }));
    setPackages(sortPackagesByCost(updatedPackages));
  }, [offer, experiencesByPackageId]);

  const getRegionsFromPackageData = (regions: Array<string>): Array<string> => {
    if (regions.includes('world')) {
      return ['World'];
    }
    return regions.map((region) => libRegions.getRegionByCode(region, 'luxuryescapes')?.name || region);
  };

  const generateLinkToAvailabilityRates = (pkg: App.Package, vendorId: string) => {
    if (
      isAccommodationPackage(pkg) &&
      pkg?.fk_property_id &&
      pkg?.fk_room_type_id &&
      pkg?.fk_room_rate_id &&
      vendorId
    ) {
      // TypeScript now knows pkg is AccommodationPackage
      return (
        <Link
          to={`/vendors/${vendorId}/properties/${pkg.fk_property_id}/room-types/${pkg.fk_room_type_id}/room-rates/${pkg.fk_room_rate_id}`}
          target="_blank"
        >
          View Availability &amp; Rates&nbsp;
          <LaunchIcon sx={{ position: 'relative', top: '0.25rem' }} />
        </Link>
      );
    }
    return 'Missing Data To Get Availability & Rates';
  };

  const generateLinkToComplimentaryExperience = (experience: App.Experience) => (
    <Box
      key={experience.id}
      sx={{
        color: experience.inactive ? grey[600] : 'inherit',
      }}
    >
      <Link to={`/experience/${experience.id}`} target="_blank">
        {experience.inactive ? `(INACTIVE) ${experience.title}` : experience.title}
        <LaunchIcon sx={{ position: 'relative', top: '0.25rem' }} />
      </Link>
    </Box>
  );

  const getPackageById = (id: string | undefined): PackageWithExperiences | undefined => {
    const packagesById = arrayToObject(offer.result.packages, (pkg) => pkg.le_package_id);
    return packagesById[id ?? ''];
  };

  const { result: offerResult } = offer;
  const packageType = offerResult.type;
  let title = 'Packages';
  let link = `/edit-offers/${offerResult.id_salesforce_external}`;
  if (packageType === OFFER_TYPE_BUNDLE_AND_SAVE) {
    title = `Bundle ${title}`;
    link = `${link}/bundle-packages`;
  } else {
    link = `${link}/packages`;
  }

  const showMapPackageUpgrades = isLuxLoyaltyEnabled() && isOfferTypeHotel(offerResult);

  return (
    <Box>
      <Typography component="h1" variant="h3" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {title}
        <Link to={link}>
          <IconButton aria-label="Edit" size="small">
            <EditIcon />
          </IconButton>
        </Link>
      </Typography>
      {packages?.map((packageData) => {
        const targetUpgradePackage = getPackageById(packageData.target_upgrade_package_id);
        return (
          <Accordion key={packageData.le_package_id} sx={{ border: '1px solid #ddd' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-content"
              id="panel-header"
              sx={{ borderBottom: 0 }}
            >
              <Typography variant="body1" data-meta={packageData.le_package_id}>
                {getPackageName(packageData)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={isHotelOfferType(packageType) || isTourOfferType(packageType) ? 6 : 12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      IDs
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '1rem' }}>
                      <CopyableField value={packageData.le_package_id} />
                      <CopyableField value={packageData.id_salesforce_external} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                      Inclusion
                    </Typography>
                    <Typography variant="body1">{packageData.description}</Typography>
                  </Grid>
                  {isHotelOfferType(packageType) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {isAccommodationPackage(packageData) &&
                        packageData.fk_property_id &&
                        offerResult.vendor_account_id ? (
                          <Link
                            to={`/vendors/${offerResult.vendor_account_id}/properties/${packageData.fk_property_id}`}
                          >
                            Property
                          </Link>
                        ) : (
                          'Property Link Not Found'
                        )}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {packageData && offerResult.vendor_account_id
                          ? generateLinkToAvailabilityRates(packageData, offerResult.vendor_account_id)
                          : 'Missing Data To Get Availability & Rates'}
                      </Typography>
                    </Grid>
                  )}
                  {isTourOfferType(packageType) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {isTourPackage(packageData) && packageData.fk_tour_id && offerResult.vendor_account_id ? (
                          <Link to={`/vendors/${offerResult.vendor_account_id}/tours/${packageData.fk_tour_id}`}>
                            Tour
                          </Link>
                        ) : (
                          'Tour Link Not Found'
                        )}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {isAccommodationPackage(packageData) && packageType !== OFFER_TYPE_BUNDLE_AND_SAVE && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        <abbr title="Number">No</abbr> of nights
                      </Typography>
                      <Typography variant="body1">{packageData.number_of_nights} Nights</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Cost Price
                      </Typography>
                      <Typography variant="body1">
                        {packageData.cost_currency}{' '}
                        {currencyFormatter.format(packageData.cost_price, { code: packageData.cost_currency })}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Package Regions
                    </Typography>
                    <Typography variant="body1">
                      {getRegionsFromPackageData(packageData.regions || ['world']).map((country) => (
                        <span key={country}>{country} </span>
                      ))}
                    </Typography>
                  </Grid>
                  {packageData.addons && packageData.addons.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Add-Ons Available
                      </Typography>
                      <Typography variant="body1">
                        {packageData.addons.map((addon) => addon.name).join(', ')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Complementary Experience
                    </Typography>
                    {packageData?.svc_experiences?.length > 0 ? (
                      packageData.svc_experiences.map((experience) => generateLinkToComplimentaryExperience(experience))
                    ) : (
                      <Typography variant="body1">No</Typography>
                    )}
                  </Grid>
                </Grid>
                {showMapPackageUpgrades && targetUpgradePackage && (
                  <Box>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        (LEGACY) Target Upgrade Package ({LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName})
                      </Typography>
                      <Tooltip title="Warning">
                        <IconButton onClick={() => setShowPackageTargetUpgradesWarning(true)}>
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                      <LuxLoyaltyPackageTargetUpgradesWarning
                        open={showPackageTargetUpgradesWarning || showPackageTargetUpgradesDevWarning}
                        onClose={() => setShowPackageTargetUpgradesWarning(false)}
                        onClick={() => setShowPackageTargetUpgradesDevWarning(false)}
                        showDevWarning={showPackageTargetUpgradesDevWarning}
                      />
                    </Stack>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box>Name: </Box>
                        <CopyableField value={getPackageName(targetUpgradePackage)} />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box>Package ID: </Box>
                        <CopyableField value={targetUpgradePackage.le_package_id} />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box>Salesforce ID: </Box>
                        <CopyableField value={targetUpgradePackage.id_salesforce_external} />
                      </Stack>
                    </Stack>
                  </Box>
                )}
                {showMapPackageUpgrades && 'package_target_upgrades' in packageData && (
                  <Box>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        (DO NOT USE) Package Target Upgrades ({LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName})
                      </Typography>
                      <Tooltip title="Warning">
                        <IconButton onClick={() => setShowPackageTargetUpgradesDevWarning(true)}>
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Package ID</TableCell>
                            <TableCell>Salesforce ID</TableCell>
                            <TableCell>Upgrade Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {packageData.package_target_upgrades.map((upgrade: any, index: number) => {
                            const targetPackage = getPackageById(upgrade.target_package_id);
                            if (!targetPackage) {
                              return null;
                            }

                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <CopyableField value={getPackageName(targetPackage)} />
                                </TableCell>
                                <TableCell>
                                  <CopyableField value={targetPackage.le_package_id} />
                                </TableCell>
                                <TableCell>
                                  <CopyableField value={targetPackage.id_salesforce_external} />
                                </TableCell>
                                <TableCell>{upgrade.upgrade_type}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
